import { useEffect, useMemo } from "react";
import { ErrorMessage, Field, Formik, useFormikContext } from "formik";
import { faClipboardList } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import _, { debounce, throttle } from "lodash";
import { useReservationCustomFieldsQuery } from "../../../api/reservation.api"
import { useEventState } from "../functions/event-state"
import { useReservationState } from "../functions/reservation-state";
import { getCustomFieldSize, isCheckBox, isCheckBoxList, isDropdownList, isInputField, isMultifield, isRadioButtonList, isTextAreaField } from "../functions/reservation-functions";
import { Reservation, ReservationCustomField, ReservationCustomFieldGroup, ReservationCustomFieldType } from "../../../api/reservation.models";

interface AutoSaveStateProps {
	group: ReservationCustomFieldGroup;
}
const AutoSaveState = ({ group }: AutoSaveStateProps) => {
	const { event } = useEventState();
	const { reservation, updateReservation, useReservationValidationListener } = useReservationState();
	const { data: customFieldGroups } = useReservationCustomFieldsQuery(event?.Id ?? 0);
	const { values, submitForm, isValidating } = useFormikContext();

	useReservationValidationListener(() => {
		submitForm();
	});

	// const update = useMemo(() => throttle((res: Reservation, values: any) => {	
	const update = debounce((reservation: Reservation, values: any) => {
		for (let customFieldGroup of customFieldGroups?.filter(g => g.Id === group.Id) ?? []) {
			for (let customField of customFieldGroup.CustomFields) {
				if (reservation.Custom === undefined)
					reservation.Custom = [];
				var reservationCustomField = reservation.Custom.find(c => c.CustomFieldId === customField.Id);
				if (reservationCustomField) {
					reservationCustomField.Value = _.get(values, `customField_${customField.Id}`) ?? "";
				} else {
					if (customField.Type == ReservationCustomFieldType.CheckBoxSmall || 
						customField.Type == ReservationCustomFieldType.CheckBoxMedium ||
						customField.Type == ReservationCustomFieldType.CheckBoxLarge) {
						reservation.Custom.push({
							CustomFieldGroupId: customFieldGroup.Id,
							CustomFieldId: customField.Id,
							Value: false
						})
					} else {
						reservation.Custom.push({
							CustomFieldGroupId: customFieldGroup.Id,
							CustomFieldId: customField.Id,
							Value: _.get(values, `customField_${customField.Id}`)
						})
					}
				}
			}
		}

		updateReservation(reservation);
	}, 100);
	// }, 1000), []);
	
	useEffect(() => {
		update(reservation, values);
	}, [isValidating])
	
	return <></>
}

const EventReservationCustomFields = () => {
	const { event } = useEventState();
	const { reservation } = useReservationState();
	const { data: customFieldGroups } = useReservationCustomFieldsQuery(event?.Id ?? 0);

	const getInitialValues = (customFieldGroup: ReservationCustomFieldGroup) => {
		var values = {};

		for (let field of customFieldGroup.CustomFields) {
			var value = reservation?.Custom?.find(c => c.CustomFieldId === field.Id);
			if (value)
				_.set(values, `customField_${field.Id}`, value.Value);
			else {
				if (isMultifield(field)) {
					_.set(values, `customField_${field.Id}`, []);
				} else {
					_.set(values, `customField_${field.Id}`, '');
				}
			}
		}

		return values;
	}

	const validate = (values: any, customFieldGroup: ReservationCustomFieldGroup) => { 
		var errors = {};

		for (let field of customFieldGroup.CustomFields) {
			if (field.Required && (_.get(values, `customField_${field.Id}`) === undefined || _.get(values, `customField_${field.Id}`).length === 0)) {
				_.set(errors, `customField_${field.Id}`, 'Required');
			}
		}

		return errors;
	}

	return <>
		{customFieldGroups?.map((customFieldGroup, groupIndex) => { 
		return <div className="card shadow mb-3" key={groupIndex}>
			<h5 className="card-header"><FontAwesomeIcon icon={faClipboardList} className="text-muted mr-2" />{customFieldGroup.Title}</h5>

			<div className="card-body">
				<p>{customFieldGroup.Subtitle}</p>

				<Formik
					// validationSchema={AdditionalInformationSchema}
					// enableReinitialize={true}
					initialValues={getInitialValues(customFieldGroup)}
					onSubmit={() => { }}
					validateOnBlur={true}
					validate={(values) => validate(values, customFieldGroup)}
				>
				{() => <>
					<AutoSaveState group={customFieldGroup} />

					<div className="row">
						{customFieldGroup.CustomFields.map((customField, customFieldIndex) => {
							return <div className={getCustomFieldSize(customField)} key={customFieldIndex}>
								<CustomField field={customField} group={customFieldGroup} />
							</div>
						})}
					</div>

				</>}
				</Formik>

			</div>
		</div>
		})}
	</>
}

export default EventReservationCustomFields

interface CustomFieldProps {
	field: ReservationCustomField;
	group: ReservationCustomFieldGroup;
}

const CustomField = ({ field, group }: CustomFieldProps) => {
	const { getFieldMeta, getFieldHelpers } = useFormikContext();
	const { value } = getFieldMeta<string|string[]|boolean>(`customField_${field.Id}`);
	const { setValue } = getFieldHelpers(`customField_${field.Id}`);

	if (isMultifield(field) && !Array.isArray(value)) {
		setValue([]);
	}

	const isSelected = (item: string) => {
		if (isRadioButtonList(field))
			return value === item;

		if (isCheckBoxList(field) && Array.isArray(value))
			return (value as string[]).includes(item);

		return false;
	}

	const setSelected = (item: string, selected: boolean) => {
		var values = _.clone(value);
		if (isCheckBoxList(field) && Array.isArray(values)) {
			if (selected !== true && values.includes(item) === false) {
				values.push(item);
			}
			if (selected === true && values.includes(item) === true) {
				values.splice(values.indexOf(item), 1);
			}
		}
		if (isRadioButtonList(field)) {
			values = item;
		}
		setValue(values);
	}

	return <>
		{isInputField(field) &&
			<div className="form-label-group mb-2">
				<Field type="text" name={`customField_${field.Id}`} className={classNames([`form-control`, field.Required ? 'required':'' ])} placeholder="Please Specify" autoComplete="off" />
				<label className="mt-2 mt-sm-0 text-truncate">{field.Title}</label>
				<ErrorMessage name={`customField_${field.Id}`} component="div" className="fw-bold small text-danger error-message" />
			</div>
		}
		{isTextAreaField(field) &&
			<div className="form-label-group mb-2">
				<Field component="textarea" rows="3" name={`customField_${field.Id}`} className={classNames([`form-control`, field.Required ? 'required':'' ])} placeholder="Please Specify" autoComplete="off" />
				<label className="mt-2 mt-sm-0 text-truncate">{field.Title}</label>
				<ErrorMessage name={`customField_${field.Id}`} component="div" className="fw-bold small text-danger error-message" />
			</div>
		}
		{isDropdownList(field) &&
			<div className="form-label-group mb-2">
				<Field component="select" name={`customField_${field.Id}`} className={classNames(["custom-select", value !== "" ? "value-selected" : "" ])}>
					{(value === "" || value === undefined) && <option></option>}
					{field.Items.map((item, index) => {
						return <option value={item} key={index}>{item}</option>
					})}
				</Field>
				<label className="text-truncate">{field.Title}</label>
				<ErrorMessage name={`customField_${field.Id}`} component="div" className="fw-bold small text-danger error-message" />
			</div>
		}
		{isCheckBox(field) &&
			<div className="custom-control custom-checkbox mt-2 mb-2">					
				<Field type="checkbox" name={`customField_${field.Id}`} id={`customField_${field.Id}`} checked={value === true} className="custom-control-input" />
				<label htmlFor={`customField_${field.Id}`} className="custom-control-label">{field.Title}</label>
				{field.Required && ((value as boolean) === false) &&
					<div className="small text-danger error-message mt-1">Required</div>
				}
			</div>	
		}
		{isCheckBoxList(field) &&
			<div className="form-group">
				<div className="mb-2">
					<p className="text-muted mb-0">{field.Title}</p>
				</div>
				{field.Items.map((item, index) => {
					return <div key={index} className="custom-control custom-checkbox mb-2">
						<Field type="checkbox" name={`customField_${field.Id}`} id={`customField_${field.Id}_${index}`} checked={isSelected(item)} onChange={(e: any) => setSelected(item, !e.target.checked)} className="custom-control-input" />
						<label htmlFor={`customField_${field.Id}_${index}`} className="custom-control-label">{item}</label>
					</div>
				})}
				{field.Required && ((value as string[]).length === 0) &&
					<div className="small text-danger error-message mt-1">Please make a selection</div>
				}
			</div>	
		}
		{isRadioButtonList(field) &&
			<div className="form-group">
				<div className="mb-2">
					<p className="text-muted mb-0">{field.Title}</p>
				</div>
				{field.Items.map((item, index) => {
					return <div key={index} className="custom-control custom-radio mb-2">
						<Field type="radio" name={`customField_${field.Id}`} id={`customField_${field.Id}_${index}`} checked={isSelected(item)} onChange={(e: any) => setSelected(item, !e.target.checked)} className="custom-control-input" />
						<label htmlFor={`customField_${field.Id}_${index}`} className="custom-control-label">{item}</label>
					</div>
				})}
				{field.Required && ((value as string) === '') &&
					<div className="small text-danger error-message mt-1">Please make a selection</div>
				}
			</div>	
		}
	</>
}